/**
 * Výber hrany — jedno okno pre celú appku (KOLO 201).
 *
 * Do KOLA 201 boli tri: `SlotEdgePickerDialog` (hrany zákazky a detegované
 * bez hľadania a bez cien, celý cenník až za tlačidlom), `HranaPickerDialog`
 * (cenník s hrúbkou pásky natvrdo 0,5 / 1 / 2 — 0,45 sa nedala nájsť)
 * a vlastný zoznam vo Vymeniť hranu, ktorý hranu vymenil hneď po kliku.
 *
 * Teraz jedna tabuľka so skupinami:
 * - **V zákazke** — hrany už použité vo variante, zo strán dielca navrchu
 *   so štítkom „na dielci" (otvor sa olepuje tou istou páskou ako obvod),
 * - **Odporúčané pre materiál** — detegované pre dosku po kurácii slotu
 *   (tenká / hrubá, manuál),
 * - **Ostatné z cenníka** — hľadanie v celom cenníku, šírka podľa hrúbky
 *   dielca ako kapsula s ✕ — „28 mm a širšie", lebo širšia hrana dosku
 *   olepí tiež (KOLO 222).
 *
 * Bez materiálu (hromadná úprava, cenovka) len cenník so šírkou a hrúbkou
 * pásky v rozbaľovačkách — šírka po triedach (Úzka, Stredná, …), nie po
 * jednotlivých šírkach (KOLO 222). Hranu z cenníka, ktorú materiál neponúka, sa dá
 * zároveň pridať k hranám materiálu (kurácia slotu) — nabudúce bude medzi
 * odporúčanými.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  useEdgeCandidates,
  useEdgeWidths,
  useMaterialDetail,
  type SirkaTrieda,
} from "@/api/hooks/useCennik";
import { useVariantaEdgesSummary } from "@/api/hooks/useVarianty";
import { useBoardThicknesses } from "@/api/hooks/useBoardThicknesses";
import { useSlotEdgeCuration } from "@/api/hooks/useSlotEdges";
import { extractErrorMessage } from "@/api/client";
import { toast } from "@/components/ui/use-toast";
import {
  MaterialSelect,
  StitokRiadku,
  type SkupinaPoloziek,
} from "@/components/slot/MaterialSelect";
import { FILTER_TLACIDLO, FILTER_ZAPNUTY } from "@/components/vyber/KategoriaVyberu";
import { VyberDialog } from "@/components/vyber/VyberDialog";
import { cisloMm, materialPrimaryId } from "@/lib/format";
import { cn } from "@/lib/utils";

const VSETKY = "__vsetky__";

/**
 * Hrúbky ABS pásky do rozbaľovačky. Endpoint s hrúbkami z cenníka nie je
 * (šírky áno — `edge-widths`), tak je to zoznam, ktorý pokryje Hranipex
 * aj Egger — 0,45 a 0,8 do KOLA 201 chýbali.
 */
const HRUBKY_PASKY = ["0.4", "0.45", "0.5", "0.8", "1", "1.5", "2", "3"] as const;

export interface VyberHranyDialogProps {
  open: boolean;
  onClose: () => void;
  /** Názov príde, keď ho okno pozná (toast výmeny). */
  onSelect: (id: string, nazov?: string) => void;
  /** Hrana, ktorá je teraz — v okne označená. */
  currentId: string | null;
  nadpis?: string;
  popis?: ReactNode;
  /** Text tlačidla v pätičke. */
  potvrdText?: ReactNode;
  /** Doska — bez nej nie sú odporúčané, len cenník. */
  slotMaterialId?: string;
  slotId?: string;
  hrubaHranaMm?: string;
  /** ID varianty — skupina hrán už použitých v zákazke. */
  variantaId?: string;
  /** Hrúbka dosky dielca — šírka pásky z Nastavenia → Hrúbky dosiek. */
  zMm?: number;
  /** Hrany na stranách dielca — v „V zákazke" navrchu so štítkom. */
  naDielci?: readonly string[];
  /** Bez „Pridať aj k hranám materiálu" (pridaná hrana, výmena). */
  bezKuracie?: boolean;
  /** Hrana, ktorá sa ponúknuť nesmie — tá, ktorá sa vymieňa. */
  vynechat?: readonly string[];
  /** Pri zápise do viacerých dielcov — tlačidlo čaká. */
  busy?: boolean;
}

export function VyberHranyDialog({
  open,
  onClose,
  onSelect,
  currentId,
  nadpis = "Vybrať hranu",
  popis,
  potvrdText = "Vybrať",
  slotMaterialId,
  slotId,
  hrubaHranaMm,
  variantaId,
  zMm,
  naDielci,
  bezKuracie = false,
  vynechat,
  busy = false,
}: VyberHranyDialogProps): JSX.Element {
  const [picked, setPicked] = useState<string | null>(currentId);
  const [pridatKMaterialu, setPridatKMaterialu] = useState(false);
  const [sirka, setSirka] = useState<SirkaTrieda | typeof VSETKY>(VSETKY);
  const [hrubka, setHrubka] = useState<string>(VSETKY);

  useEffect(() => {
    if (open) {
      setPicked(currentId);
      setPridatKMaterialu(false);
      setSirka(VSETKY);
      setHrubka(VSETKY);
    }
  }, [open, currentId]);

  const sMaterialom = Boolean(slotMaterialId);
  const curation = useSlotEdgeCuration(slotId ?? "");
  const hrubkyQuery = useBoardThicknesses();
  const sirkaPreDosku = (hrubkyQuery.data?.data ?? []).find(
    (b) => zMm != null && b.hrubka_mm === Math.round(zMm),
  )?.edge_width_mm;
  const candidatesQuery = useEdgeCandidates(slotMaterialId, hrubaHranaMm, slotId);
  const usedQuery = useVariantaEdgesSummary(variantaId);
  const doskaQuery = useMaterialDetail(slotMaterialId);
  // Ten istý dopyt ako pás Vybrané v pätičke — názov pre volajúceho
  const vybranaQuery = useMaterialDetail(picked ?? undefined);
  const widthsQuery = useEdgeWidths({ enabled: open && !sMaterialom });

  /**
   * Triedy šírky v poradí zo zoznamu šírok (od úzkej po blok). User:
   * *„pri hranách nedávaj dané šírky, ale len tie rozsahy"* (KOLO 222).
   * Bez počtov — zoznam šírok ráta celý cenník, výber len ponúkateľné.
   */
  const triedy = useMemo(() => {
    const out = new Map<SirkaTrieda, string>();
    for (const w of widthsQuery.data?.data ?? []) {
      if (!out.has(w.pasmo_kod)) out.set(w.pasmo_kod, w.pasmo_label);
    }
    return out;
  }, [widthsQuery.data]);

  const naDielciSet = useMemo(() => new Set(naDielci ?? []), [naDielci]);
  const allCandidates = candidatesQuery.data?.data?.candidates ?? [];
  const vPoole = new Set([
    ...allCandidates.map((c) => c.edge.id),
    ...(candidatesQuery.data?.data?.pool_ids ?? []),
  ]);

  const doska = doskaQuery.data?.data;
  const kodDosky = doska ? (materialPrimaryId(doska) ?? doska.nazov) : null;

  const skupiny: SkupinaPoloziek[] = useMemo(() => {
    const kandidati = candidatesQuery.data?.data?.candidates ?? [];
    const podlaId = new Map(kandidati.map((c) => [c.edge.id, c]));
    /** Rola a kurácia — aj pri hrane zo zákazky, ktorá je odporúčaná. */
    const stitkyKandidata = (id: string): ReactNode => {
      const c = podlaId.get(id);
      if (!c) return null;
      const hrubky = c.usage.used_for_hrubky ?? [];
      const rola = c.usage.role === "tenka" ? "tenká" : c.usage.role === "hruba" ? "hrubá" : c.usage.role;
      return (
        <>
          {rola && hrubky.length > 0 ? (
            <StitokRiadku title={`Appka ju vyberie sama pre dielce ${hrubky.join(", ")} mm`}>
              {`${rola} · ${hrubky.join(", ")} mm`}
            </StitokRiadku>
          ) : null}
          {c.usage.curation === "add" ? (
            <StitokRiadku title="Pridaná k hranám materiálu ručne">manuál</StitokRiadku>
          ) : null}
        </>
      );
    };
    // Hrany zo strán dielca navrch — poradie inak ostáva podľa použitia
    const pouzite = [...(usedQuery.data?.data?.edges ?? [])].sort(
      (a, b) => Number(naDielciSet.has(b.edge.id)) - Number(naDielciSet.has(a.edge.id)),
    );
    return [
      {
        kluc: "zakazka",
        nadpis: "V zákazke",
        polozky: pouzite.map((row) => ({
          material: row.edge,
          stitky: (
            <>
              {naDielciSet.has(row.edge.id) ? (
                <StitokRiadku vyrazny title="Hrana je na niektorej strane tohto dielca">
                  na dielci
                </StitokRiadku>
              ) : null}
              {stitkyKandidata(row.edge.id)}
            </>
          ),
          info: `${row.pocet_dielcov}× · ${cisloMm(row.dlzka_m)} m`,
        })),
      },
      {
        kluc: "odporucane",
        nadpis: kodDosky ? `Odporúčané pre materiál ${kodDosky}` : "Odporúčané pre materiál",
        // Hrana, ktorá je už v zákazke, sa tu neopakuje (MaterialSelect)
        polozky: kandidati.map((c) => ({ material: c.edge, stitky: stitkyKandidata(c.edge.id) })),
      },
    ];
  }, [usedQuery.data, candidatesQuery.data, naDielciSet, kodDosky]);

  /** Vybraná hrana z cenníka, ktorú materiál zatiaľ neponúka. */
  const mimoMaterialu = Boolean(picked) && sMaterialom && !vPoole.has(picked ?? "");
  const kuracia = Boolean(slotId) && !bezKuracie && sMaterialom;

  async function potvrd(id: string, nazov?: string): Promise<void> {
    if (pridatKMaterialu && kuracia && !vPoole.has(id)) {
      try {
        await curation.mutateAsync({ materialId: id, mode: "add" });
      } catch (err) {
        // Hranu na dielec dať aj tak — kurácia je len pohodlie navyše
        toast({
          variant: "destructive",
          title: "Hranu sa nepodarilo pridať k materiálu",
          description: extractErrorMessage(err),
        });
      }
    }
    const detail = vybranaQuery.data?.data;
    onSelect(id, nazov ?? (detail?.id === id ? detail.nazov : undefined));
  }

  return (
    <VyberDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      nadpis={nadpis}
      popis={
        popis ??
        (sMaterialom
          ? "Najprv hrany už použité v zákazke, pod nimi odporúčané pre materiál, potom celý cenník."
          : "Hrana z cenníka — šírku a hrúbku pásky vyber filtrom alebo napíš do hľadania (22x1, dekor, kód).")
      }
      vybraneId={picked}
      potvrdText={potvrdText}
      potvrdZakazane={busy}
      onPotvrd={() => picked && void potvrd(picked)}
      akcie={
        kuracia ? (
          <label
            className={cn(
              "flex items-center gap-2 text-sm",
              mimoMaterialu ? "cursor-pointer" : "text-muted-foreground",
            )}
          >
            <input
              type="checkbox"
              checked={pridatKMaterialu}
              onChange={(e) => setPridatKMaterialu(e.target.checked)}
              disabled={!mimoMaterialu}
              className="h-4 w-4 accent-primary"
            />
            Pridať aj k hranám materiálu — nabudúce sa ponúkne medzi odporúčanými
          </label>
        ) : null
      }
    >
      <MaterialSelect
        vOkne
        value={picked}
        onChange={(id) => {
          setPicked(id);
          setPridatKMaterialu(false);
        }}
        onPotvrd={(m) => {
          if (!busy) void potvrd(m.id, m.nazov);
        }}
        druh="dlzkovy"
        sekcia="hrany"
        placeholder="Vyhľadať hranu (názov, kód, dekor, 22x1)…"
        skupiny={sMaterialom || variantaId ? skupiny : undefined}
        vynechat={vynechat}
        infoNadpis={variantaId ? "Použitie" : undefined}
        sirkaPostacuje={sMaterialom && sirkaPreDosku ? String(sirkaPreDosku) : undefined}
        sirkaTrieda={!sMaterialom && sirka !== VSETKY ? sirka : undefined}
        hrubkaMm={!sMaterialom && hrubka !== VSETKY ? hrubka : undefined}
        // Bez materiálu sú šírka a hrúbka v rozbaľovačkách, nie v kapsulách
        rozmerKapsule={sMaterialom}
        pripraveny={!(sMaterialom && zMm != null && hrubkyQuery.isLoading)}
        filtreNavyse={
          sMaterialom ? null : (
            <>
              <Select
                value={sirka}
                onValueChange={(v) => setSirka(v as SirkaTrieda | typeof VSETKY)}
              >
                <SelectTrigger
                  aria-label="Filter šírky hrany"
                  className={cn(FILTER_TLACIDLO, sirka !== VSETKY && FILTER_ZAPNUTY)}
                >
                  <span>
                    {sirka === VSETKY ? "Šírka" : `Šírka: ${triedy.get(sirka) ?? sirka}`}
                  </span>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={VSETKY}>Všetky šírky</SelectItem>
                  {[...triedy].map(([kod, label]) => (
                    <SelectItem key={kod} value={kod}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={hrubka} onValueChange={setHrubka}>
                <SelectTrigger
                  aria-label="Filter hrúbky hrany"
                  className={cn(FILTER_TLACIDLO, hrubka !== VSETKY && FILTER_ZAPNUTY)}
                >
                  <span>{hrubka === VSETKY ? "Hrúbka pásky" : `Hrúbka: ${cisloMm(hrubka)} mm`}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={VSETKY}>Všetky hrúbky</SelectItem>
                  {HRUBKY_PASKY.map((h) => (
                    <SelectItem key={h} value={h}>
                      {cisloMm(h)} mm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )
        }
      />
    </VyberDialog>
  );
}
